import axios from "axios";
import Swal from "sweetalert2";

// --- Payment type mapping ---
export const TypePaiement = (type) => {
  switch (type) {
    case "Espèces":
    case "Espéces":
    case "Especes":
    case "Cash":
    case "cash":
      return "cash";
    case "Carte bancaire":
    case "creditcard":
      return "bank_card";
    case "Tickets cadeaux":
    case "giftcard":
      return "restaurant_ticket";
    case "bon":
      return "contre_bon";
    case "Chéque":
      return "check";
    default:
      return "unknown";
  }
};

const urlNacef = process.env.REACT_APP_NACEF_PROXY;

// --- Convert price to millimes ---
const convertToMil = (prix) => Math.round((prix ?? 0) * 1000);

// --- UTF-8 safe Base64 encoding ---
const toBase64Utf8 = (str) => {
  return btoa(
    encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, p1) =>
      String.fromCharCode(parseInt(p1, 16))
    )
  );
};

export const signatureRequest = async (data) => {
  try {
    if (!data || !data.carts || !data.payments) {
      throw new Error("Données de ticket manquantes");
    }
    const timbre = data.timbre;
    const timbreMil = convertToMil(data.timbre || 0);

    // --- Construct products ---
    const listProduct = data.carts.map((element, index) => {
      const tva = Number((element.productInfo?.tva ?? 19).toFixed(2));
      return {
        product: {
          family_code: "F001",
          name: element.productInfo?.titre?.fr || `Produit ${index + 1}`,
          price_pre_tax: convertToMil(element.productInfo?.prix_ht),
        },
        taxation: [
          {
            type: "percent",
            value: tva,
            tax_code: tva === 19 ? "TVA19" : tva === 7 ? "TVA7" : "TAXE_ADD",
          },
        ],
        quantity: element.quantity ?? 1,
        discount_per_unit: {
          percent: 0,
          value: 0,
        },
      };
    });

    // --- Calculate totals ---
    const totals = listProduct.reduce(
      (acc, item) => {
        const priceHT = item.product.price_pre_tax / 1000;
        const qty = item.quantity;
        const discount =
          item.discount_per_unit.percent > 0
            ? (priceHT * item.discount_per_unit.percent) / 100
            : item.discount_per_unit.value / 1000;
        const totalHTItem = (convertToMil(priceHT) - discount) * qty;

        let totalTaxItem = 0;
        item.taxation.forEach((tax) => {
          totalTaxItem += totalHTItem * (tax.value / 100);
        });

        acc.total_ht += totalHTItem;
        acc.total_tax += totalTaxItem;
        return acc;
      },
      { total_ht: 0, total_tax: 0 }
    );
    totals.total_ht = totals.total_ht * 1000; // in millimes
    totals.total_tax = totals.total_tax + timbreMil;
    totals.total_inc_tax = totals.total_ht + totals.total_tax;

    // --- Tax summary ---
    const taxSummary = [];

    if (timbreMil > 0) {
      taxSummary.push({
        tax_code: "TAXE_ADD",
        total_amount: timbreMil,
      });
    }

    listProduct.forEach((item) => {
      item.taxation.forEach((tax) => {
        const taxAmount =
         Number(item.product.price_pre_tax *
          item.quantity *
          (tax.value / 100)) ;
        const existing = taxSummary.find((t) => t.tax_code === tax.tax_code);
        if (existing) existing.total_amount += Number(taxAmount);
        else
          taxSummary.push({ tax_code: tax.tax_code, total_amount: taxAmount });
      });
    });

    // --- Additional tax percentage ---
    const Per_Tax_ADD =
      totals.total_ht > 0 ? (timbreMil / totals.total_ht) * 100 : 0;
    // --- Payments ---
    const listPaiement = data.payments.map((p) => ({
      method: TypePaiement(p.payment_method),
      amount: convertToMil(p.amount),
    }));

    const listRendu = data.payments
      .filter((p) => p.rendu && p.rendu !== 0)
      .map((p) => ({
        method: TypePaiement(p.payment_method),
        amount: convertToMil(p.rendu),
      }));

    // --- Merchant info ---
    const mf = (data.restaurant?.matricule_fiscale ?? "1587609T")
      .replace(/[^a-zA-Z0-9]/g, "")
      .slice(0, 8);

    // --- Construct ticket ---
    const ticketTest = {
      data_type: "ncf.cashier.operation",
      version: "1.1.3",
      transaction: {
        id: data.table_session?.code || "UNKNOWN",
        timestamp: data.table_session?.end_time || new Date().toISOString(),
        operation: {
          op_type: "TICKET",
          context: "SALE",
        },
        originator: {
          agent_identifier: data.caissier?.code || "001",
          imdf: "20251608560004",
          cash_register_serialnumber: "PF4E5GBG",
          cash_register_software: "POSv2024",
          accreditation_reference: "ACC123456",
        },
      },
      merchant_identity: {
        id: mf || "1587609T",
        id_type: "MF",
        taxpayer_establishment: {
          commercial_name: data.restaurant?.nom || "clediss",
          reference: "000",
          address: data.restaurant?.address,
          city: data.restaurant?.city || "Tunis",
        },
      },
      customer_identity: {
        id_type: "NP",
      },
      sale_details: listProduct,
      tax_summary: taxSummary,
      general_discount: {
        percent: 0,
        value: 0,
      },
      additional_tax: {
        type: "percent",
        value: Per_Tax_ADD,
        tax_code: "TAXE_ADD",
      },
      payment_details: {
        collection_details: listPaiement,
        returned_change: listRendu,
      },
      sale_summary: {
        total_excl_tax: totals.total_ht,
        total_incl_tax: totals.total_inc_tax,
        total_tax: totals.total_tax,
      },
      delivery_details: {
        type: "SELF_PICKUP",
      },
    };

    // --- Encode ticket in Base64 (UTF-8 safe) ---
    const json = JSON.stringify(ticketTest);
    const base64DATASinf = toBase64Utf8(json);

    const data_sigTicket = {
      base64Ticket: base64DATASinf,
      totalHT: totals.total_ht,
      totalTax: totals.total_tax,
      operationType: "Vente",
      transactionType: "Copie",
    };
    console.log("ticketTest", JSON.stringify(ticketTest));
    // --- Send to NACEF ---
   /* const response = await axios.post(
      `${urlNacef}/api/homologation/signatureRequestApi`,
      data_sigTicket
    );

    const result = response.data;

    if (result.errorCode === 0) {
      Swal.fire({
        icon: "success",
        title: "Succès",
        text: "Ticket signé avec succès !",
        width: 600,
      });
      return { success: true, data: result };
    } else {
      Swal.fire({
        icon: "error",
        title: "Échec",
        text: result.message || "Erreur NACEF",
        width: 600,
      });
      return { success: false, message: result.message, data: result };
    }*/
  } catch (err) {
    const result = err.response?.data;
    const msg = result?.message || err.message || "Erreur inconnue";
    Swal.fire({
      icon: "error",
      title: "Erreur NACEF",
      text: msg,
      html: result?.object
        ? `<pre style="text-align:left">${JSON.stringify(
            result.object,
            null,
            2
          )}</pre>`
        : undefined,
      width: 600,
    });
    return { success: false, message: msg, data: result?.object };
  }
};
